import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import { resultsApi } from '../../api/resultsApi';
import type { Category, Competition, Discipline, Team } from '../../types/domain';
import { ResultEntryDialog } from '../../components/results/ResultEntryDialog';
import { ResultsTable } from '../../components/results/ResultsTable';
import { EditCompletedAtDialog } from '../../components/results/EditCompletedAtDialog';
import { PdfPrintButton } from '../../components/results/PdfPrintButton';
import { generateDisciplineRanking } from '../../utils/pdf/generateDisciplineRanking';
import { generateDvojbojRanking } from '../../utils/pdf/generateDvojbojRanking';
import { useDvojbojRanking } from '../../api/hooks/useDvojbojRanking';
import { useAuth } from '../../hooks/useAuth';
import { ResultHistoryDialog } from '../../components/audit/ResultHistoryDialog';
import { athleteApi } from '../../api/athleteApi';
import { IndividualResultEntryDialog } from '../../components/results/IndividualResultEntryDialog';
import { IndividualRankingTable } from '../../components/results/IndividualRankingTable';
import { TeamSumRankingTable } from '../../components/results/TeamSumRankingTable';
import { individualResultsApi } from '../../api/individualResultsApi';
import type { Athlete, IndividualRankingItem } from '../../types/domain';

interface Props {
  competition: Competition;
}

export const CompetitionResultsTab = ({ competition }: Props) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'UNIT_ADMIN' || user?.role === 'SUPER_ADMIN';
  const [editCompletedAt, setEditCompletedAt] = useState<{ resultId: number; completedAt: string | null } | null>(null);
  const [categoryIdx, setCategoryIdx] = useState(0);
  const [disciplineIdx, setDisciplineIdx] = useState(0);
  const [entryTeam, setEntryTeam] = useState<Team | null>(null);
  const [entryAttempt, setEntryAttempt] = useState(1);
  const [historyResultId, setHistoryResultId] = useState<number | null>(null);
  const [individualSubTab, setIndividualSubTab] = useState<'individual' | 'team-sum'>('individual');
  const [editingAthlete, setEditingAthlete] = useState<Athlete | null>(null);
  const [, setEditingItem] = useState<IndividualRankingItem | null>(null);

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['admin', 'competition', competition.id, 'categories'],
    queryFn: () => adminApi.competitionCategories(competition.id),
  });

  const currentCategory: Category | undefined = categories[categoryIdx];
  const currentDiscipline: Discipline | undefined = currentCategory?.disciplines[disciplineIdx];

  useEffect(() => setDisciplineIdx(0), [categoryIdx]);

  const { data: teams = [] } = useQuery({
    queryKey: ['admin', 'category', currentCategory?.id, 'teams'],
    queryFn: () => (currentCategory ? adminApi.listTeams(currentCategory.id) : Promise.resolve([])),
    enabled: !!currentCategory,
  });

  const { data: results = [] } = useQuery({
    queryKey: ['admin', 'results', competition.id],
    queryFn: () => resultsApi.listByCompetition(competition.id),
    refetchInterval: 5_000,
  });

  const { data: allAthletes = [] } = useQuery({
    queryKey: ['competition', competition.id, 'athletes'],
    queryFn: () => athleteApi.listByCompetition(competition.id),
  });

  const { data: individualRanking = [] } = useQuery({
    queryKey: ['individual-ranking', currentDiscipline?.id],
    queryFn: () =>
      currentDiscipline
        ? individualResultsApi.getIndividualRanking(currentDiscipline.id)
        : Promise.resolve([]),
    enabled: !!currentDiscipline?.isIndividual,
    refetchInterval: 5_000,
  });

  const filteredResults = currentDiscipline
    ? results.filter((r) => r.disciplineId === currentDiscipline.id)
    : [];

  // Dvojboj — nacitame jen pokud aktualni kategorie ma >=2 disciplin s isDvojbojDisc
  const dvojbojDisciplines = (currentCategory?.disciplines ?? []).filter((d) => d.isDvojbojDisc);
  const hasDvojboj = dvojbojDisciplines.length >= 2;
  const { data: dvojbojData = [] } = useDvojbojRanking(hasDvojboj ? currentCategory?.id ?? null : null);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (categories.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">Soutěž zatím nemá kategorie.</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={2}>
      <Tabs
        value={categoryIdx}
        onChange={(_, v) => setCategoryIdx(v)}
        variant={categories.length > 4 ? 'scrollable' : 'fullWidth'}
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            textTransform: 'uppercase',
            fontWeight: 700,
            letterSpacing: '0.05em',
            fontSize: '0.9375rem',
            minHeight: 56,
            flex: categories.length > 4 ? undefined : 1,
          },
        }}
      >
        {categories.map((c) => (
          <Tab key={c.id} label={c.name} />
        ))}
      </Tabs>

      {currentCategory && isAdmin && hasDvojboj && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <PdfPrintButton
            label="Tisk dvojboj"
            size="small"
            disabled={dvojbojData.length === 0}
            onGenerate={() =>
              generateDvojbojRanking({
                competition,
                category: currentCategory,
                rankings: dvojbojData,
                disciplineNames: dvojbojDisciplines.map((d) => d.name),
              })
            }
          />
        </Stack>
      )}

      {currentCategory && (
        <>
          <Tabs
            value={disciplineIdx}
            onChange={(_, v) => setDisciplineIdx(v)}
            variant={currentCategory.disciplines.length > 4 ? 'scrollable' : 'fullWidth'}
            scrollButtons="auto"
            textColor="secondary"
            indicatorColor="secondary"
            sx={{
              '& .MuiTab-root': {
                flex: currentCategory.disciplines.length > 4 ? undefined : 1,
              },
            }}
          >
            {currentCategory.disciplines.map((d) => (
              <Tab key={d.id} label={d.name} />
            ))}
          </Tabs>

          {currentDiscipline && currentDiscipline.isIndividual && (
            <Stack spacing={2}>
              {currentDiscipline.teamScoringCount && (
                <Tabs
                  value={individualSubTab}
                  onChange={(_, v) => setIndividualSubTab(v as 'individual' | 'team-sum')}
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab value="individual" label="Jednotlivci" />
                  <Tab value="team-sum" label="Družstvo (součet)" />
                </Tabs>
              )}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Závodníci (
                        {allAthletes.filter((a) => teams.some((t) => t.id === a.teamId)).length})
                      </Typography>
                      <Stack spacing={1}>
                        {allAthletes
                          .filter((a) => teams.some((t) => t.id === a.teamId))
                          .sort((x, y) => x.lastName.localeCompare(y.lastName))
                          .map((a) => (
                            <Card key={a.id} variant="outlined">
                              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Typography fontWeight={600}>
                                      {a.lastName} {a.firstName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {teams.find((t) => t.id === a.teamId)?.name ?? ''}
                                    </Typography>
                                  </Box>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => {
                                      setEditingAthlete(a);
                                      const existing = individualRanking.find(
                                        (it) => it.athleteId === a.id,
                                      );
                                      setEditingItem(existing ?? null);
                                    }}
                                  >
                                    Zadat čas
                                  </Button>
                                </Stack>
                              </CardContent>
                            </Card>
                          ))}
                        {allAthletes.filter((a) => teams.some((t) => t.id === a.teamId)).length === 0 && (
                          <Typography color="text.secondary" variant="body2">
                            Družstva v této kategorii nemají žádné závodníky. Přidej je v tabu „Závodníci".
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 7 }}>
                  <Card>
                    <CardContent>
                      {individualSubTab === 'individual' && (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Pořadí jednotlivců — {currentDiscipline.name}
                          </Typography>
                          <IndividualRankingTable
                            discipline={currentDiscipline}
                            onEdit={(item) => {
                              const a = allAthletes.find((x) => x.id === item.athleteId);
                              if (a) {
                                setEditingAthlete(a);
                                setEditingItem(item);
                              }
                            }}
                          />
                        </>
                      )}
                      {individualSubTab === 'team-sum' && currentDiscipline.teamScoringCount && (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Pořadí družstev — {currentDiscipline.name}
                          </Typography>
                          <TeamSumRankingTable discipline={currentDiscipline} />
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <IndividualResultEntryDialog
                open={editingAthlete != null}
                athlete={editingAthlete}
                discipline={currentDiscipline}
                competition={competition}
                onClose={() => {
                  setEditingAthlete(null);
                  setEditingItem(null);
                }}
              />
            </Stack>
          )}

          {currentDiscipline && !currentDiscipline.isIndividual && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 5 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Družstva ({teams.length})
                    </Typography>
                    {teams.length === 0 && (
                      <Typography color="text.secondary" variant="body2">
                        Nejsou přidána žádná družstva.
                      </Typography>
                    )}
                    <Stack spacing={1}>
                      {teams.map((t) => {
                        const teamResults = filteredResults.filter((r) => r.teamId === t.id);
                        const hasAttempts = teamResults.length > 0;
                        const nextAttempt = hasAttempts
                          ? Math.min(currentDiscipline.attemptsCount, teamResults.length + 1)
                          : 1;
                        return (
                          <Card key={t.id} variant="outlined">
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Chip label={`#${t.startNumber}`} size="small" />
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                  <Typography fontWeight={600}>{t.name}</Typography>
                                  {hasAttempts && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      {teamResults.length}/{currentDiscipline.attemptsCount} pokus
                                    </Typography>
                                  )}
                                  {teamResults
                                    .filter((r) => r.isNp && r.note && r.note.trim())
                                    .map((r) => (
                                      <Typography
                                        key={r.id}
                                        variant="caption"
                                        color="error.main"
                                        sx={{
                                          display: 'block',
                                          fontStyle: 'italic',
                                          lineHeight: 1.3,
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {currentDiscipline.attemptsCount > 1
                                          ? `NP (pokus ${r.attemptNumber}): ${r.note}`
                                          : `NP: ${r.note}`}
                                      </Typography>
                                    ))}
                                </Box>
                                {currentDiscipline.attemptsCount > 1 ? (
                                  <Stack direction="row" spacing={0.5}>
                                    {Array.from({ length: currentDiscipline.attemptsCount }).map((_, i) => {
                                      const att = teamResults.find((r) => r.attemptNumber === i + 1);
                                      const hasData = !!att && (att.isNp || att.timeFinalMs != null);
                                      return (
                                        <Button
                                          key={i}
                                          size="small"
                                          variant={hasData ? 'outlined' : 'contained'}
                                          color={att?.isNp ? 'error' : 'primary'}
                                          onClick={() => {
                                            setEntryTeam(t);
                                            setEntryAttempt(i + 1);
                                          }}
                                          sx={{
                                            flexDirection: 'column',
                                            lineHeight: 1.15,
                                            minWidth: 90,
                                            py: 0.5,
                                          }}
                                        >
                                          <Box
                                            component="span"
                                            sx={{
                                              fontSize: '0.65rem',
                                              fontWeight: 500,
                                              opacity: 0.75,
                                              letterSpacing: '0.02em',
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            pokus {i + 1}
                                          </Box>
                                          <Box
                                            component="span"
                                            sx={{ fontSize: '0.95rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                                          >
                                            {!att
                                              ? '—'
                                              : att.isNp
                                                ? 'NP'
                                                : att.timeFinalMs != null
                                                  ? att.timeFinalFormatted
                                                  : '—'}
                                          </Box>
                                        </Button>
                                      );
                                    })}
                                  </Stack>
                                ) : (() => {
                                  const att = teamResults[0];
                                  const hasData = !!att && (att.isNp || att.timeFinalMs != null);
                                  return (
                                    <Button
                                      size="small"
                                      variant={hasData ? 'outlined' : 'contained'}
                                      color={att?.isNp ? 'error' : 'primary'}
                                      onClick={() => {
                                        setEntryTeam(t);
                                        setEntryAttempt(nextAttempt);
                                      }}
                                      sx={{
                                        flexDirection: 'column',
                                        lineHeight: 1.15,
                                        minWidth: 90,
                                        py: 0.5,
                                      }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          fontSize: '0.65rem',
                                          fontWeight: 500,
                                          opacity: 0.75,
                                          letterSpacing: '0.02em',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        pokus 1
                                      </Box>
                                      <Box
                                        component="span"
                                        sx={{ fontSize: '0.95rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                                      >
                                        {!att
                                          ? '—'
                                          : att.isNp
                                            ? 'NP'
                                            : att.timeFinalMs != null
                                              ? att.timeFinalFormatted
                                              : '—'}
                                      </Box>
                                    </Button>
                                  );
                                })()}
                              </Stack>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 7 }}>
                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="h6">
                        Pořadí — {currentDiscipline.name}
                      </Typography>
                      {isAdmin && currentCategory && (
                        <PdfPrintButton
                          label="Tisk PDF"
                          size="small"
                          disabled={filteredResults.length === 0}
                          onGenerate={() =>
                            generateDisciplineRanking({
                              competition,
                              category: currentCategory,
                              discipline: currentDiscipline,
                              results: filteredResults,
                            })
                          }
                        />
                      )}
                    </Stack>
                    <ResultsTable
                      results={filteredResults}
                      showAttempt={currentDiscipline.attemptsCount > 1}
                      onHistoryClick={(id) => setHistoryResultId(id)}
                      timeFormat={currentDiscipline.timeFormat}
                      showCompletedAt={isAdmin}
                      onEditCompletedAt={
                        isAdmin
                          ? (resultId, completedAt) => setEditCompletedAt({ resultId, completedAt })
                          : undefined
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}

      <ResultEntryDialog
        open={!!entryTeam}
        team={entryTeam}
        discipline={currentDiscipline ?? null}
        category={currentCategory ?? null}
        attemptNumber={entryAttempt}
        parallelTracks={competition.stafetaDvojicParallelTracks}
        competitionId={competition.id}
        penaltyTrackingMode={competition.penaltyTrackingMode}
        existing={
          entryTeam && currentDiscipline
            ? filteredResults.find((r) => r.teamId === entryTeam.id && r.attemptNumber === entryAttempt) ?? null
            : null
        }
        onClose={() => setEntryTeam(null)}
      />

      <ResultHistoryDialog
        open={historyResultId !== null}
        resultId={historyResultId}
        onClose={() => setHistoryResultId(null)}
      />

      <EditCompletedAtDialog
        open={editCompletedAt !== null}
        onClose={() => setEditCompletedAt(null)}
        resultId={editCompletedAt?.resultId ?? null}
        competitionId={competition.id}
        initialValue={editCompletedAt?.completedAt ?? null}
      />
    </Stack>
  );
};
